const REFRESH_TIMEOUT_MS = 8000;
const IMAGE_REFRESH_ATTEMPTS = 2;

/**
 * Reusable private-attachment flow for stable application URLs:
 * - refresh on image load error
 * - optional inline activation using the existing image element
 * - optional session-blocked callback + unavailable renderer
 *
 * @example
 * const hydratePrivateAttachmentLinks = initPrivateAttachmentLinks({
 *   root: notesSection,
 *   imageSelector: '[data-game-note-attachment-image]',
 *   linkSelector: '[data-game-note-attachment-link]',
 *   refreshUrlDataAttribute: 'refreshUrl',
 *   onActivate: ({ link, image }) => openPreview(link, image),
 *   onSessionBlocked: showSessionAlert,
 *   renderUnavailable: ({ link }) => {
 *     link.textContent = 'Unavailable';
 *   },
 * });
 * hydratePrivateAttachmentLinks?.(appendedRoot);
 *
 * @param {{
 *   root: ParentNode,
 *   imageSelector: string,
 *   linkSelector?: string,
 *   refreshUrlDataAttribute?: string,
 *   onActivate?: (context: {
 *     link: HTMLAnchorElement,
 *     image: HTMLImageElement,
 *     activateLink: (link: HTMLAnchorElement) => boolean,
 *   }) => boolean,
 *   onSessionBlocked?: () => void,
 *   renderUnavailable?: (context: { link: HTMLAnchorElement, image: HTMLImageElement }) => void,
 * }} options
 * @returns {((root: ParentNode) => void)|null}
 */
export function initPrivateAttachmentLinks(options) {
    const root = options?.root;
    const imageSelector = String(options?.imageSelector ?? '').trim();
    const linkSelector = String(options?.linkSelector ?? 'a').trim();
    const refreshUrlDataAttribute = String(options?.refreshUrlDataAttribute ?? 'refreshUrl').trim();
    const onActivate = typeof options?.onActivate === 'function' ? options.onActivate : null;
    const onSessionBlocked = typeof options?.onSessionBlocked === 'function'
        ? options.onSessionBlocked
        : () => {};
    const renderUnavailable = typeof options?.renderUnavailable === 'function'
        ? options.renderUnavailable
        : renderUnavailableByDefault;

    if (!isQueryRoot(root) || imageSelector === '' || linkSelector === '' || refreshUrlDataAttribute === '') {
        return null;
    }

    const state = {
        sessionRefreshBlocked: false,
        inFlightRefreshByKey: new Map(),
        recoveringImages: new WeakSet(),
    };

    const hydrate = (hydrateRoot) => {
        if (!isQueryRoot(hydrateRoot)) {
            return;
        }

        const attachmentImages = hydrateRoot.querySelectorAll(imageSelector);
        attachmentImages.forEach((imageNode) => {
            if (!(imageNode instanceof HTMLImageElement)) {
                return;
            }

            if (imageNode.dataset.privateAttachmentBound === '1') {
                return;
            }
            imageNode.dataset.privateAttachmentBound = '1';

            const link = resolveAttachmentLink(imageNode, linkSelector);
            if (link instanceof HTMLAnchorElement) {
                link.addEventListener('click', (event) => {
                    handleAttachmentLinkClick({
                        event,
                        imageSelector,
                        onActivate,
                    });
                });
            }

            imageNode.addEventListener('error', (event) => {
                void handleAttachmentLoadError({
                    event,
                    state,
                    refreshUrlDataAttribute,
                    onSessionBlocked,
                    renderUnavailable,
                    linkSelector,
                });
            });

            if (imageNode.complete && imageNode.naturalWidth === 0) {
                imageNode.dispatchEvent(new Event('error'));
            }
        });
    };

    hydrate(root);

    return hydrate;
}

function isQueryRoot(value) {
    return value instanceof Document
        || value instanceof DocumentFragment
        || value instanceof HTMLElement;
}

function parseJsonSafely(response) {
    return response.json().catch(() => null);
}

function parseUrlSafely(url) {
    try {
        return new URL(url, window.location.origin);
    } catch (_error) {
        return null;
    }
}

function isValidAttachmentRefreshUrl(candidateUrl, referenceUrl) {
    const parsedCandidate = parseUrlSafely(candidateUrl);
    const parsedReference = parseUrlSafely(referenceUrl);
    if (parsedCandidate === null || parsedReference === null) {
        return false;
    }

    if (parsedCandidate.origin !== window.location.origin) {
        return false;
    }

    return parsedCandidate.pathname === parsedReference.pathname;
}

function resolveAttachmentLink(image, linkSelector) {
    const link = image.closest(linkSelector);

    return link instanceof HTMLAnchorElement ? link : null;
}

function resolveAttachmentImage(link, imageSelector) {
    const image = link.querySelector(imageSelector);

    return image instanceof HTMLImageElement ? image : null;
}

function readRefreshUrl(image, refreshUrlDataAttribute) {
    const datasetValue = image.dataset[refreshUrlDataAttribute];

    return String(datasetValue ?? '').trim();
}

function markSessionBlocked(state, onSessionBlocked) {
    if (state.sessionRefreshBlocked) {
        onSessionBlocked();
        return;
    }

    state.sessionRefreshBlocked = true;
    onSessionBlocked();
}

function renderUnavailableByDefault({ link }) {
    link.removeAttribute('href');
    link.setAttribute('aria-disabled', 'true');
    link.removeAttribute('target');
    link.removeAttribute('rel');
    link.textContent = 'Unavailable';
}

function applyRefreshedUrl(image, link, refreshedUrl) {
    if (!(image instanceof HTMLImageElement) || typeof refreshedUrl !== 'string' || refreshedUrl.trim() === '') {
        return false;
    }

    const normalizedUrl = refreshedUrl.trim();
    image.src = normalizedUrl;
    if (link instanceof HTMLAnchorElement) {
        link.href = normalizedUrl;
    }

    return true;
}

function renderAttachmentUnavailable({ image, linkSelector, renderUnavailable }) {
    if (!(image instanceof HTMLImageElement)) {
        return;
    }

    const link = resolveAttachmentLink(image, linkSelector);
    if (!(link instanceof HTMLAnchorElement)) {
        return;
    }

    renderUnavailable({ link, image });
}

async function refreshAttachmentUrl({
    image,
    state,
    refreshAttempt,
    refreshUrlDataAttribute,
    onSessionBlocked,
    linkSelector,
}) {
    if (!(image instanceof HTMLImageElement)) {
        return null;
    }

    const refreshUrl = readRefreshUrl(image, refreshUrlDataAttribute);
    if (refreshUrl === '') {
        return null;
    }

    if (state.sessionRefreshBlocked) {
        onSessionBlocked();
        return null;
    }

    const existingInFlight = state.inFlightRefreshByKey.get(refreshUrl);
    if (existingInFlight instanceof Promise) {
        return existingInFlight;
    }

    const refreshPromise = (async () => {
        const abortController = new AbortController();
        const timeoutHandle = window.setTimeout(() => {
            abortController.abort();
        }, REFRESH_TIMEOUT_MS);

        try {
            const response = await fetch(refreshUrl, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
                signal: abortController.signal,
            });
            const payload = await parseJsonSafely(response);

            if (response.status === 401) {
                markSessionBlocked(state, onSessionBlocked);
                return null;
            }

            if (
                !response.ok
                || typeof payload !== 'object'
                || payload === null
                || payload.success !== true
                || typeof payload.url !== 'string'
                || payload.url.trim() === ''
            ) {
                return null;
            }

            const refreshedUrl = payload.url.trim();
            const link = resolveAttachmentLink(image, linkSelector);
            const referenceHref = link instanceof HTMLAnchorElement ? String(link.href) : String(image.src);

            if (!isValidAttachmentRefreshUrl(refreshedUrl, referenceHref)) {
                return null;
            }

            const cacheBustedUrl = parseUrlSafely(refreshedUrl);
            if (cacheBustedUrl === null) {
                return null;
            }
            cacheBustedUrl.searchParams.set('retry', `${Date.now()}-${refreshAttempt}`);

            return cacheBustedUrl.toString();
        } catch (_error) {
            return null;
        } finally {
            window.clearTimeout(timeoutHandle);
            state.inFlightRefreshByKey.delete(refreshUrl);
        }
    })();

    state.inFlightRefreshByKey.set(refreshUrl, refreshPromise);

    return refreshPromise;
}

async function handleAttachmentLoadError({
    event,
    state,
    refreshUrlDataAttribute,
    onSessionBlocked,
    renderUnavailable,
    linkSelector,
}) {
    const image = event.currentTarget;
    if (!(image instanceof HTMLImageElement)) {
        return;
    }

    if (state.recoveringImages.has(image)) {
        return;
    }
    state.recoveringImages.add(image);

    try {
        let refreshAttempts = Number.parseInt(image.dataset.refreshAttempts ?? '0', 10);
        if (!Number.isFinite(refreshAttempts)) {
            refreshAttempts = 0;
        }

        if (refreshAttempts >= IMAGE_REFRESH_ATTEMPTS) {
            renderAttachmentUnavailable({ image, linkSelector, renderUnavailable });
            return;
        }

        while (refreshAttempts < IMAGE_REFRESH_ATTEMPTS) {
            refreshAttempts += 1;
            image.dataset.refreshAttempts = String(refreshAttempts);
            const refreshedUrl = await refreshAttachmentUrl({
                image,
                state,
                refreshAttempt: refreshAttempts,
                refreshUrlDataAttribute,
                onSessionBlocked,
                linkSelector,
            });
            const link = resolveAttachmentLink(image, linkSelector);
            if (applyRefreshedUrl(image, link, refreshedUrl)) {
                return;
            }
            if (state.sessionRefreshBlocked) {
                break;
            }
        }

        renderAttachmentUnavailable({ image, linkSelector, renderUnavailable });
    } finally {
        state.recoveringImages.delete(image);
    }
}

function handleAttachmentLinkClick({
    event,
    imageSelector,
    onActivate,
}) {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) {
        return;
    }

    if (onActivate === null || !isPlainPrimaryActivation(event)) {
        return;
    }

    const activated = activateInlineAttachment({
        link,
        imageSelector,
        onActivate,
    });

    if (activated) {
        event.preventDefault();
    }
}

function activateInlineAttachment({
    link,
    imageSelector,
    onActivate,
}) {
    if (!(link instanceof HTMLAnchorElement)) {
        return false;
    }

    const image = resolveAttachmentImage(link, imageSelector);
    if (!(image instanceof HTMLImageElement)) {
        return false;
    }

    const activateLink = (candidateLink) => activateInlineAttachment({
        link: candidateLink,
        imageSelector,
        onActivate,
    });

    try {
        return onActivate({ link, image, activateLink }) === true;
    } catch (_error) {
        return false;
    }
}

function isPlainPrimaryActivation(event) {
    return event.defaultPrevented !== true
        && (typeof event.button !== 'number' || event.button === 0)
        && event.metaKey !== true
        && event.ctrlKey !== true
        && event.shiftKey !== true
        && event.altKey !== true;
}
